import {
  evaluatePasswordStrength,
  type PasswordStrengthLevel,
  passwordUserInputs,
} from '@pulsify/shared/password'
import { pwnedPassword } from 'hibp'
import { Info } from 'lucide-react'
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'

interface PasswordStrengthProps {
  password: string
  userInputs?: string[]
  className?: string
}

export function PasswordStrength({ password, userInputs = [], className }: PasswordStrengthProps) {
  const [isPwned, setIsPwned] = useState<number | null>(null)
  const [isCheckingPwned, setIsCheckingPwned] = useState(false)
  const deferredPassword = useDeferredValue(password)
  const userInputsKey = passwordUserInputs(userInputs).join('\0')
  const strength = useMemo(() => {
    if (!deferredPassword) return null
    const normalizedUserInputs = userInputsKey ? userInputsKey.split('\0') : []
    return evaluatePasswordStrength(deferredPassword, normalizedUserInputs)
  }, [deferredPassword, userInputsKey])

  useEffect(() => {
    if (!deferredPassword || deferredPassword.length < 4) {
      setIsPwned(null)
      setIsCheckingPwned(false)
      return
    }

    let cancelled = false
    setIsCheckingPwned(true)
    const timer = setTimeout(async () => {
      try {
        const numPwns = await pwnedPassword(deferredPassword)
        if (!cancelled) setIsPwned(numPwns)
      } catch (error) {
        console.error('Failed to check password against HIBP:', error)
        if (!cancelled) setIsPwned(null)
      } finally {
        if (!cancelled) setIsCheckingPwned(false)
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
      setIsCheckingPwned(false)
    }
  }, [deferredPassword])

  const level = strength?.level ?? 'weak'
  const levelIndex: Record<PasswordStrengthLevel, number> = {
    weak: 0,
    medium: 1,
    strong: 2,
  }
  const strengthLabels: Record<PasswordStrengthLevel, string> = {
    weak: m.passwordStrengthWeak(),
    medium: m.passwordStrengthMedium(),
    strong: m.passwordStrengthStrong(),
  }
  const strengthColors: Record<PasswordStrengthLevel, string> = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
  }

  const hasPassword = password.length > 0
  const isCompromised = isPwned !== null && isPwned > 0
  const hasFeedback = isCompromised || (strength !== null && !strength.isAcceptable)
  const filledLevel = isCompromised ? 0 : levelIndex[level]

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex gap-1">
        {[0, 1, 2].map((level) => (
          <div
            key={level}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors duration-200',
              isCompromised
                ? 'bg-red-500'
                : hasPassword && level <= filledLevel
                  ? strengthColors[strength?.level ?? 'weak']
                  : 'bg-gray-200 dark:bg-gray-700',
            )}
          />
        ))}
      </div>
      {hasPassword && (
        <div className="flex items-center gap-1.5">
          <p
            className={cn(
              'text-xs font-medium transition-colors duration-200',
              isCompromised
                ? 'text-red-600 dark:text-red-400'
                : level === 'weak'
                  ? 'text-red-600 dark:text-red-400'
                  : level === 'medium'
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400',
            )}
          >
            {isCompromised ? (
              m.passwordStrengthCompromised()
            ) : isCheckingPwned ? (
              m.passwordStrengthChecking()
            ) : (
              <>
                {m.passwordStrength()}: {strengthLabels[level]}
              </>
            )}
          </p>
          {hasFeedback && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1.5">
                    {isCompromised && (
                      <p className="text-xs font-medium text-destructive">
                        {m.passwordStrengthPwnedWarning({
                          count: isPwned.toLocaleString(),
                        })}
                      </p>
                    )}
                    {strength !== null && !strength.isAcceptable && (
                      <p className="text-xs font-medium">{m.weakPasswordError()}</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  )
}
